import React from "react";
import { Link } from "react-router-dom";
import {
  COMPANY_NAME,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";

// Shared pieces of the financial statements (Statement of Financial Position,
// Profit & Loss), so both read, expand and print the same way.

export const isoDate = (date) => date.toLocaleDateString("en-CA");

export const today = () => isoDate(new Date());

export const endOfLastMonth = () => {
  const now = new Date();
  return isoDate(new Date(now.getFullYear(), now.getMonth(), 0));
};

export const endOfLastYear = () =>
  isoDate(new Date(new Date().getFullYear() - 1, 11, 31));

const parseIso = (value) => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Named periods for the Profit & Loss filters. The fiscal year runs July to
// June.
export const periodPresets = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const fiscalStart = m >= 6 ? y : y - 1;
  return [
    ["This month", isoDate(new Date(y, m, 1)), isoDate(new Date(y, m + 1, 0))],
    ["Last month", isoDate(new Date(y, m - 1, 1)), isoDate(new Date(y, m, 0))],
    [
      "This fiscal year",
      isoDate(new Date(fiscalStart, 6, 1)),
      isoDate(new Date(fiscalStart + 1, 5, 30)),
    ],
    [
      "Last fiscal year",
      isoDate(new Date(fiscalStart - 1, 6, 1)),
      isoDate(new Date(fiscalStart, 5, 30)),
    ],
    ["This year", isoDate(new Date(y, 0, 1)), isoDate(new Date(y, 11, 31))],
  ];
};

const isLastDayOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getDate() ===
  1;

// The period just before [from, to]. Whole calendar months step back by the
// same number of months (all of August before all of September); any other
// range steps back by its length in days.
export const previousPeriod = (from, to) => {
  const start = parseIso(from);
  const end = parseIso(to);

  if (start.getDate() === 1 && isLastDayOfMonth(end)) {
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      end.getMonth() -
      start.getMonth() +
      1;
    return [
      isoDate(new Date(start.getFullYear(), start.getMonth() - months, 1)),
      isoDate(new Date(start.getFullYear(), start.getMonth(), 0)),
    ];
  }

  const days = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
  return [
    isoDate(
      new Date(start.getFullYear(), start.getMonth(), start.getDate() - days),
    ),
    isoDate(
      new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1),
    ),
  ];
};

export const samePeriodLastYear = (from, to) => {
  const shift = (value) => {
    const date = parseIso(value);
    return isoDate(
      new Date(date.getFullYear() - 1, date.getMonth(), date.getDate()),
    );
  };
  return [shift(from), shift(to)];
};

// Accounting style: negatives in brackets, zero as a dash.
export const amount = (value) => {
  if (value === null || value === undefined) return "";
  const n = num(value);
  if (Math.abs(n) < 0.005) return "–";
  return n < 0 ? `(${money(-n)})` : money(n);
};

export const isZero = (node) =>
  Math.abs(num(node.amount)) < 0.005 &&
  (node.compare_amount === null ||
    node.compare_amount === undefined ||
    Math.abs(num(node.compare_amount)) < 0.005);

export const nodeKey = (node) =>
  node.key
    ? `line-${node.key}`
    : node.is_profit
      ? "profit"
      : String(node.id);

// Every node that can be expanded, for "Expand all".
export const expandableKeys = (nodes, keys = []) => {
  nodes.forEach((node) => {
    if (node.children && node.children.length) {
      keys.push(nodeKey(node));
      expandableKeys(node.children, keys);
    }
  });
  return keys;
};

// Pushes a node and, when expanded, the accounts beneath it.
export const pushNodeRows = (rows, nodes, depth, expanded, hideZero) => {
  nodes
    .filter((node) => !hideZero || !isZero(node))
    .forEach((node) => {
      const key = nodeKey(node);
      const hasChildren = !!(node.children && node.children.length);
      rows.push({
        type: depth === 1 ? "line" : "account",
        key: `n-${key}-${depth}`,
        node,
        depth,
        hasChildren,
        expanded: hasChildren && !!expanded[key],
      });
      if (hasChildren && expanded[key]) {
        pushNodeRows(rows, node.children, depth + 1, expanded, hideZero);
      }
    });
};

export const rowAmounts = (row) =>
  row.node
    ? { amount: row.node.amount, compare: row.node.compare_amount }
    : { amount: row.amount, compare: row.compare_amount };

export const isNodeRow = (row) => row.type === "line" || row.type === "account";

// Particulars cell for an account row: expand toggle for parents, ledger
// link for leaf accounts.
export const NodeNameCell = ({ row, onToggle, note }) => {
  const { node, depth, hasChildren } = row;
  const indent = depth > 1 ? { paddingLeft: 32 + (depth - 1) * 20 } : {};
  const label = hasChildren ? (
    <button
      type="button"
      className="fs-name-btn"
      onClick={() => onToggle(node)}
    >
      {node.name}
    </button>
  ) : node.id ? (
    <Link
      to={`/chart-of-account-ledger/${node.id}`}
      className="fs-ledger-link"
      title="Open the account ledger"
    >
      {node.name}
    </Link>
  ) : (
    node.name
  );

  return (
    <td style={indent}>
      {hasChildren ? (
        <button
          type="button"
          className="fs-toggle"
          onClick={() => onToggle(node)}
          aria-expanded={row.expanded}
          aria-label={`${row.expanded ? "Collapse" : "Expand"} ${node.name}`}
        >
          <i
            className={`fa fa-${row.expanded ? "minus" : "plus"}`}
            aria-hidden="true"
          ></i>
        </button>
      ) : (
        <span className="fs-toggle-spacer"></span>
      )}
      {label}
      {node.status === "inactive" && (
        <span className="fs-inactive">Inactive</span>
      )}
      {note && <span className="fs-note">{note}</span>}
    </td>
  );
};

export const AmountCell = ({ value }) => (
  <td className={`coa-num ${num(value) < -0.005 ? "fs-neg" : ""}`}>
    {amount(value)}
  </td>
);

export const PresetChips = ({ presets, isActive, onPick }) => (
  <div className="fs-presets">
    {presets.map((preset) => {
      const active = isActive(preset);
      return (
        <button
          key={preset[0]}
          type="button"
          className={`coa-chip${active ? " is-active" : ""}`}
          style={active ? { backgroundColor: "#2a2962" } : undefined}
          onClick={() => onPick(preset)}
        >
          {preset[0]}
        </button>
      );
    })}
  </div>
);

// Printable copy of a statement in a new window, with the company header and
// sign-off lines.
export const printStatement = ({ title, subtitle, headHtml, bodyHtml, note }) => {
  const printWindow = window.open("", "_blank");
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 24px; color: #22242f; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #2a2962; padding-bottom: 12px; }
        .company { margin: 0; font-size: 22px; color: #2a2962; }
        .title { margin: 6px 0 2px; font-size: 16px; }
        .period { margin: 0; font-size: 12px; color: #555; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #2a2962; color: #fff; padding: 7px 8px; font-size: 11px; text-transform: uppercase; text-align: left; }
        td { padding: 5px 8px; border-bottom: 1px solid #eee; }
        .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .code { color: #777; font-size: 11px; }
        tr.section td { font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #e42320; border-bottom: 2px solid #2a2962; padding-top: 14px; }
        tr.group td { font-weight: bold; background: #f4f5fa; color: #2a2962; }
        tr.account td { color: #555; font-size: 11px; }
        tr.subtotal td { font-weight: bold; border-top: 1px solid #999; }
        tr.total td { font-weight: bold; background: #e9ebf5; border-top: 1px solid #2a2962; }
        tr.grand td { font-weight: bold; font-size: 13px; border-top: 2px solid #2a2962; border-bottom: 4px double #2a2962; }
        .note { margin-top: 10px; font-size: 11px; color: #555; }
        .signatures { display: flex; justify-content: space-between; margin-top: 70px; font-size: 12px; }
        .signatures div { width: 28%; text-align: center; border-top: 1px solid #333; padding-top: 6px; }
        @media print { body { margin: 0; } tr { page-break-inside: avoid; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="company">${COMPANY_NAME}</h1>
        <h3 class="title">${title}</h3>
        <p class="period">${subtitle}</p>
      </div>
      <table>
        <thead><tr>${headHtml}</tr></thead>
        <tbody>${bodyHtml}</tbody>
      </table>
      ${note ? `<p class="note">${note}</p>` : ""}
      <div class="signatures">
        <div>Prepared by</div>
        <div>Checked by</div>
        <div>Approved by</div>
      </div>
      <script>
        window.onload = function () {
          window.print();
          window.onafterprint = function () { window.close(); };
        };
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

export const FinancialStatementStyles = () => (
  <style>
    {`
      .fs-presets { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
      .fs-table { width: 100%; margin: 0; border-collapse: collapse; }
      .fs-table thead th {
        padding: 11px 16px;
        background: #2a2962;
        color: #fff;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
        border: 0;
      }
      .fs-table thead th.coa-num { text-align: right; }
      .fs-table td {
        padding: 8px 16px;
        border-bottom: 1px solid #f0f1f6;
        font-size: 0.86rem;
        vertical-align: middle;
      }
      .fs-table td.coa-num { white-space: nowrap; width: 160px; }
      .fs-table td.fs-pct { width: 90px; color: #8b90a5; font-size: 0.78rem; }
      .fs-code {
        width: 90px;
        color: #8b90a5;
        font-size: 0.76rem;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      }

      .fs-section td {
        padding-top: 18px;
        background: #fff;
        font-size: 0.8rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #e42320;
        border-bottom: 2px solid #2a2962;
      }
      .fs-group td {
        font-weight: 700;
        color: #2a2962;
        background: #f7f8fc;
      }
      .fs-line td:first-child { padding-left: 32px; }
      .fs-line:hover td, .fs-account:hover td { background: #fafbfd; }
      .fs-account td { font-size: 0.8rem; color: #52566b; }
      .fs-subtotal td {
        font-weight: 700;
        color: #2a2962;
        background: #f7f8fc;
        border-top: 1px solid #b6bacb;
      }
      .fs-subtotal td:first-child { padding-left: 32px; }
      .fs-total td {
        font-weight: 700;
        color: #fff;
        background: #2a2962;
        border-bottom: 0;
      }
      .fs-total td.fs-pct { color: #d7dae8; }
      .fs-grand td {
        font-size: 0.95rem;
        font-weight: 800;
        color: #2a2962;
        background: #eef0f8;
        border-top: 2px solid #2a2962;
        border-bottom: 4px double #2a2962;
      }

      .fs-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        margin-right: 6px;
        padding: 0;
        border: 0;
        border-radius: 4px;
        background: #eef0f8;
        color: #2a2962;
        font-size: 0.7rem;
        cursor: pointer;
        vertical-align: middle;
      }
      .fs-toggle:hover { background: #2a2962; color: #fff; }
      .fs-toggle-spacer { display: inline-block; width: 24px; }
      .fs-name-btn {
        padding: 0;
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
      }
      .fs-ledger-link { color: inherit; }
      .fs-ledger-link:hover { color: #e42320; text-decoration: underline; }
      .fs-inactive {
        margin-left: 6px;
        padding: 1px 6px;
        border-radius: 8px;
        background: #f5f6fa;
        color: #8b90a5;
        font-size: 0.64rem;
        font-weight: 600;
        text-transform: uppercase;
      }
      .fs-note {
        display: block;
        padding-left: 24px;
        font-size: 0.7rem;
        font-weight: 400;
        color: #8b90a5;
      }
      .fs-neg { color: #e42320; }
      .fs-total .fs-neg { color: #ffd2d0; }

      .coa-panel .fs-switch {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        font-size: 0.78rem;
        font-weight: 600;
        color: #52566b;
        text-transform: none;
        letter-spacing: 0;
        cursor: pointer;
      }

      .fs-skeleton {
        height: 34px;
        margin: 8px 16px;
        border-radius: 6px;
        background: linear-gradient(90deg, #f2f3f9 25%, #fafbfd 50%, #f2f3f9 75%);
        background-size: 200% 100%;
        animation: fs-shimmer 1.2s ease-in-out infinite;
      }
      @keyframes fs-shimmer {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
      }
    `}
  </style>
);
